import axios, {
  AxiosHeaders,
  AxiosRequestConfig,
  AxiosResponse,
  Method
} from 'axios'

type HeaderValue = string | number | boolean | null | undefined

export interface LegacyHeaders {
  append: (name: string, value: HeaderValue) => void;
  delete: (name: string) => void;
  get: (name: string) => string | null;
  has: (name: string) => boolean;
  set: (name: string, value: HeaderValue) => void;
  toJSON: () => Record<string, string>;
}

export interface LegacyHttpResponse<T = any> {
  body: T;
  bodyText: string;
  data: T;
  detalhesErroSentry?: object;
  metodo?: string;
  headers: LegacyHeaders;
  ok: boolean;
  status: number;
  statusText: string;
  url: string;
}

export interface LegacyHttpRequest {
  abort: () => void;
  body?: any;
  emulateJSON?: boolean;
  headers: LegacyHeaders;
  method: string;
  params?: Record<string, any>;
  rawBody?: any;
  signal: AbortSignal;
  timeout?: number;
  url: string;
}

type LegacyResponseHandler = (response: LegacyHttpResponse) => void
type LegacyNext = (handler?: LegacyResponseHandler) => void
type LegacyInterceptor = (request: LegacyHttpRequest, next: LegacyNext) => void

type LegacyHttpOptions = {
  before?: (request: LegacyHttpRequest) => void;
  emulateJSON?: boolean;
  headers?: Record<string, HeaderValue>;
  params?: Record<string, any>;
  signal?: AbortSignal;
  timeout?: number;
}

type LegacyResourceMethod = (
  params?: any,
  body?: any,
  options?: LegacyHttpOptions
) => Promise<LegacyHttpResponse>

type LegacyResource = LegacyResourceMethod & {
  delete: LegacyResourceMethod;
  get: LegacyResourceMethod;
  patch: LegacyResourceMethod;
  post: LegacyResourceMethod;
  remove: LegacyResourceMethod;
  save: LegacyResourceMethod;
  update: LegacyResourceMethod;
}

const OPTION_KEYS = new Set([
  'before',
  'emulateJSON',
  'headers',
  'params',
  'signal',
  'timeout'
])

const METHODS_WITH_IMPLICIT_BODY = new Set(['POST', 'PUT', 'PATCH'])
const METHODS_SUPPORTING_BODY = new Set(['DELETE', 'POST', 'PUT', 'PATCH'])
const RESOURCE_ACTIONS: Record<string, { method: Method }> = {
  delete: { method: 'DELETE' },
  get: { method: 'GET' },
  patch: { method: 'PATCH' },
  post: { method: 'POST' },
  remove: { method: 'DELETE' },
  save: { method: 'POST' },
  update: { method: 'PUT' }
}

const extractTemplateKeys = (template: string): string[] => {
  const matches = template.match(/\{[^}]+\}/g) || []
  return matches.flatMap((match) => {
    const content = match.slice(1, -1)
    return content
      .replace(/^[/?&]/, '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
  })
}

const hasValueForTemplate = (value: any): boolean => {
  return value !== undefined && value !== null && value !== ''
}

const createHeaders = (initialHeaders?: Record<string, HeaderValue>): LegacyHeaders => {
  const nativeHeaders = new Headers()

  if (initialHeaders) {
    Object.entries(initialHeaders).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        nativeHeaders.set(key, String(value))
      }
    })
  }

  return {
    append (name, value) {
      if (value !== undefined && value !== null) {
        nativeHeaders.append(name, String(value))
      }
    },
    delete (name) {
      nativeHeaders.delete(name)
    },
    get (name) {
      return nativeHeaders.get(name)
    },
    has (name) {
      return nativeHeaders.has(name)
    },
    set (name, value) {
      if (value === undefined || value === null) {
        nativeHeaders.delete(name)
        return
      }
      nativeHeaders.set(name, String(value))
    },
    toJSON () {
      const result: Record<string, string> = {}
      nativeHeaders.forEach((value, key) => {
        result[key] = value
      })
      return result
    }
  }
}

const normalizeAxiosHeaders = (headers: AxiosResponse['headers'] | undefined): LegacyHeaders => {
  const normalized: Record<string, HeaderValue> = {}
  if (headers) {
    Object.entries(headers).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        normalized[key] = value.join(', ')
      } else if (value !== undefined) {
        normalized[key] = value as string
      }
    })
  }
  return createHeaders(normalized)
}

const serializeBodyText = (body: any): string => {
  if (body == null) {
    return ''
  }

  if (typeof body === 'string') {
    return body
  }

  try {
    return JSON.stringify(body)
  } catch {
    return String(body)
  }
}

const normalizeResponse = (response: AxiosResponse): LegacyHttpResponse => ({
  body: response.data,
  bodyText: serializeBodyText(response.data),
  data: response.data,
  headers: normalizeAxiosHeaders(response.headers),
  ok: response.status >= 200 && response.status < 300,
  status: response.status,
  statusText: response.statusText,
  metodo: response.config.method?.toUpperCase(),
  url: response.config.url || ''
})

export const expandTemplate = (template: string, params: Record<string, any> = {}): string => {
  const consumedKeys = new Set<string>()

  const expanded = template.replace(/\{([^}]+)\}/g, (_, rawExpression: string) => {
    const expression = rawExpression.trim()

    if (expression.startsWith('/')) {
      const key = expression.slice(1)
      const value = params[key]
      if (value === undefined || value === null || value === '') {
        return ''
      }
      consumedKeys.add(key)
      return `/${encodeURIComponent(String(value))}`
    }

    if (expression.startsWith('?')) {
      const keys = expression
        .slice(1)
        .split(',')
        .map((item: string) => item.trim())
        .filter(Boolean)

      const query = keys.flatMap((key) => {
        const value = params[key]
        if (value === undefined || value === null || value === '') {
          return []
        }
        consumedKeys.add(key)
        return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
      })

      return query.length ? `?${query.join('&')}` : ''
    }

    const value = params[expression]
    if (value === undefined || value === null || value === '') {
      return ''
    }

    consumedKeys.add(expression)
    return encodeURIComponent(String(value))
  })

  const extraParams = Object.entries(params).filter(([key, value]) => {
    return !consumedKeys.has(key) && value !== undefined && value !== null && value !== ''
  })

  if (!extraParams.length) {
    return expanded
  }

  const queryString = extraParams
    .flatMap(([key, value]) => {
      if (Array.isArray(value)) {
        return value.map((item) => `${encodeURIComponent(`${key}[]`)}=${encodeURIComponent(String(item))}`)
      }
      return [`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`]
    })
    .join('&')

  return `${expanded}${expanded.includes('?') ? '&' : '?'}${queryString}`
}

const toFormUrlEncoded = (body: Record<string, any>): URLSearchParams => {
  const payload = new URLSearchParams()
  Object.entries(body || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return
    }

    if (Array.isArray(value)) {
      value.forEach((item) => payload.append(key, String(item)))
      return
    }

    payload.append(key, String(value))
  })
  return payload
}

const separateOptionsFromPayload = (value: any): { options: LegacyHttpOptions; payload: any } => {
  if (!value || value instanceof FormData || Array.isArray(value) || typeof value !== 'object') {
    return { options: {}, payload: value }
  }

  const keys = Object.keys(value)
  const hasKnownOption = keys.some((key) => OPTION_KEYS.has(key))

  if (!hasKnownOption) {
    return { options: {}, payload: value }
  }

  const options: LegacyHttpOptions = {}
  const payload: Record<string, any> = {}

  keys.forEach((key) => {
    if (OPTION_KEYS.has(key)) {
      (options as Record<string, any>)[key] = value[key]
    } else {
      payload[key] = value[key]
    }
  })

  return { options, payload }
}

export const buildResourceArguments = (
  template: string,
  method: string,
  rawParams?: any,
  rawBody?: any,
  rawOptions?: LegacyHttpOptions
) => {
  const templateKeys = new Set(extractTemplateKeys(template))
  const hasTemplateKeysInParams = Boolean(
    rawParams &&
    typeof rawParams === 'object' &&
    !Array.isArray(rawParams) &&
    !(rawParams instanceof FormData) &&
    Object.keys(rawParams).some((key) => templateKeys.has(key) && hasValueForTemplate(rawParams[key]))
  )

  let params = hasTemplateKeysInParams ? rawParams : {}
  let body = METHODS_SUPPORTING_BODY.has(method) ? rawBody : undefined
  let options = rawOptions || {}

  if (METHODS_WITH_IMPLICIT_BODY.has(method)) {
    if (rawBody === undefined && rawParams !== undefined && !hasTemplateKeysInParams) {
      const separated = separateOptionsFromPayload(rawParams)
      body = separated.payload
      options = { ...separated.options, ...options }
    } else if (rawBody !== undefined) {
      if (
        rawParams &&
        typeof rawParams === 'object' &&
        !Array.isArray(rawParams) &&
        !(rawParams instanceof FormData)
      ) {
        const separatedParams = separateOptionsFromPayload(rawParams)
        params = {
          ...params,
          ...(separatedParams.payload || {})
        }
        options = { ...separatedParams.options, ...options }
      }

      const separated = separateOptionsFromPayload(rawBody)
      body = separated.payload
      options = { ...separated.options, ...options }
    }
  } else if (method === 'DELETE') {
    if (rawParams && typeof rawParams === 'object' && !Array.isArray(rawParams) && !(rawParams instanceof FormData)) {
      const separatedParams = separateOptionsFromPayload(rawParams)
      params = {
        ...params,
        ...(separatedParams.payload || {})
      }
      options = { ...separatedParams.options, ...options }
    }

    if (rawBody !== undefined) {
      const separated = separateOptionsFromPayload(rawBody)
      body = separated.payload
      options = { ...separated.options, ...options }
    }
  } else if (rawParams && typeof rawParams === 'object') {
    const separated = separateOptionsFromPayload(rawParams)
    params = {
      ...params,
      ...(separated.payload || {})
    }
    options = { ...separated.options, ...options }
  }

  if (options.params) {
    params = { ...params, ...options.params }
  }

  return { body, options, params }
}

const defaultAxiosConfig = (): AxiosRequestConfig => ({
  headers: new AxiosHeaders(),
  transitional: {
    silentJSONParsing: true
  }
})

const createLegacyHttpClient = () => {
  const interceptors: LegacyInterceptor[] = []

  const request = async (
    method: string,
    url: string,
    body?: any,
    options: LegacyHttpOptions = {}
  ): Promise<LegacyHttpResponse> => {
    const requestHeaders = createHeaders(options.headers)
    const controller = new AbortController()

    if (options.signal) {
      if (options.signal.aborted) {
        controller.abort()
      } else {
        options.signal.addEventListener('abort', () => controller.abort(), { once: true })
      }
    }

    const requestConfig: LegacyHttpRequest = {
      abort: () => controller.abort(),
      body,
      emulateJSON: options.emulateJSON,
      headers: requestHeaders,
      method,
      params: options.params,
      rawBody: body,
      signal: controller.signal,
      timeout: options.timeout,
      url
    }

    const responseHandlers: LegacyResponseHandler[] = []

    interceptors.forEach((interceptor) => {
      interceptor(requestConfig, (handler) => {
        if (handler) {
          responseHandlers.push(handler)
        }
      })
    })

    options.before?.(requestConfig)

    const axiosConfig = defaultAxiosConfig()
    axiosConfig.method = method as Method
    axiosConfig.url = requestConfig.url
    axiosConfig.signal = requestConfig.signal
    axiosConfig.timeout = options.timeout
    axiosConfig.headers = requestHeaders.toJSON()

    if (method === 'GET') {
      axiosConfig.params = requestConfig.params
    } else if (requestConfig.emulateJSON && body && !(body instanceof FormData)) {
      axiosConfig.data = toFormUrlEncoded(body)
      axiosConfig.headers = {
        ...requestHeaders.toJSON(),
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
      }
    } else {
      axiosConfig.data = body
    }

    let normalizedResponse: LegacyHttpResponse

    try {
      const response = await axios(axiosConfig)
      normalizedResponse = normalizeResponse(response)
    } catch (error) {
      if (!axios.isAxiosError(error) || !error.response) {
        throw error
      }
      normalizedResponse = normalizeResponse(error.response)
    }

    responseHandlers.forEach((handler) => {
      handler(normalizedResponse)
    })

    if (!normalizedResponse.ok) {
      throw normalizedResponse
    }

    return normalizedResponse
  }

  const http = {
    delete: (url: string, options?: LegacyHttpOptions) => request('DELETE', url, undefined, options),
    get: (url: string, options?: LegacyHttpOptions) => request('GET', url, undefined, options),
    interceptors: {
      push (interceptor: LegacyInterceptor) {
        interceptors.push(interceptor)
        return interceptors.length
      }
    },
    patch: (url: string, body?: any, options?: LegacyHttpOptions) => request('PATCH', url, body, options),
    post: (url: string, body?: any, options?: LegacyHttpOptions) => request('POST', url, body, options),
    put: (url: string, body?: any, options?: LegacyHttpOptions) => request('PUT', url, body, options),
    request
  }

  return http
}

export const legacyHttp = createLegacyHttpClient()

export const legacyResource = (
  template: string,
  _params?: Record<string, any>,
  actions: Partial<Record<string, { method: Method }>> = {}
): LegacyResource => {
  const resourceActionMap = Object.entries({
    ...RESOURCE_ACTIONS,
    ...actions
  }).reduce<Record<string, { method: Method }>>((acc, [actionName, config]) => {
    if (config) {
      acc[actionName] = config
    }

    return acc
  }, {})

  const callable = ((params?: any, body?: any, options?: LegacyHttpOptions) => {
    return callable.get(params, body, options)
  }) as LegacyResource

  Object.entries(resourceActionMap).forEach(([actionName, config]) => {
    callable[actionName as keyof LegacyResource] = (
      params?: any,
      body?: any,
      options?: LegacyHttpOptions
    ) => {
      const method = String(config.method || 'GET').toUpperCase()
      const parsedArgs = buildResourceArguments(template, method, params, body, options)
      const expandedUrl = expandTemplate(template, parsedArgs.params)

      return legacyHttp.request(method, expandedUrl, parsedArgs.body, {
        ...parsedArgs.options,
        params: undefined
      })
    }
  })

  return callable
}

export type LegacyHttpClient = typeof legacyHttp
